/**
 * Skyyrizon Product Grid — shuffle on load, progressive reveal, quick-add.
 *
 * Reading of settings via data attributes on the grid element:
 *   data-initial-count  — how many products visible on first render
 *   data-reveal-step    — how many more to reveal per "Show more" click
 *   data-shuffle        — "true" / "false" — whether to randomize card order
 */
(function () {
  'use strict';

  var TOAST_TIMEOUT = 1800;
  var DONE_TIMEOUT = 1600;

  // ── Shuffle (Fisher-Yates) ─────────────────────────────────────

  function shuffleInPlace(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  // ── Progressive reveal ────────────────────────────────────────

  function initGrid(grid, section) {
    var initial = parseInt(grid.getAttribute('data-initial-count'), 10) || 12;
    var step = parseInt(grid.getAttribute('data-reveal-step'), 10) || 16;
    var shuffle = grid.getAttribute('data-shuffle') === 'true';

    var cards = Array.prototype.slice.call(grid.children);
    if (cards.length === 0) return;

    // 1. Shuffle on load — each refresh produces a new order
    if (shuffle) {
      var shuffled = shuffleInPlace(cards.slice());
      // Re-append in shuffled order
      shuffled.forEach(function (card) { grid.appendChild(card); });
      cards = shuffled;
    }

    // 2. Hide cards beyond the initial count
    var visibleCount = Math.min(initial, cards.length);
    cards.forEach(function (card, idx) {
      if (idx >= visibleCount) {
        card.classList.add('is-hidden');
      } else {
        card.classList.remove('is-hidden');
      }
    });

    // 3. Wire up the "Show more" button
    var moreBtn = section.querySelector('[data-sky-pgrid-more]');
    if (!moreBtn) return;

    function refreshMoreBtn() {
      if (visibleCount >= cards.length) {
        moreBtn.hidden = true;
      } else {
        moreBtn.hidden = false;
        // Update label with remaining count
        var remaining = cards.length - visibleCount;
        var toShow = Math.min(step, remaining);
        moreBtn.setAttribute('aria-label', 'Show ' + toShow + ' more products');
      }
    }

    refreshMoreBtn();

    moreBtn.addEventListener('click', function () {
      var newVisible = Math.min(visibleCount + step, cards.length);
      for (var i = visibleCount; i < newVisible; i++) {
        cards[i].classList.remove('is-hidden');
      }
      visibleCount = newVisible;
      refreshMoreBtn();
    });
  }

  // ── Quick-add via /cart/add.js ────────────────────────────────

  function showToast(section, message) {
    var toast = section.querySelector('[data-sky-pgrid-toast]');
    if (!toast) return;
    var text = toast.querySelector('.sky-pgrid__toast-text');
    if (text) text.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toast._timer);
    toast._timer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
    }, TOAST_TIMEOUT);
  }

  function setBtnState(btn, state) {
    if (state) btn.setAttribute('data-state', state);
    else btn.removeAttribute('data-state');
  }

  // Swap a Shopify section in place using its id/class
  function replaceSectionOnPage(sectionKey, newHtml) {
    if (!newHtml) return;
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(newHtml, 'text/html');
      var newSection = doc.querySelector('#shopify-section-' + sectionKey);
      var currentSection = document.getElementById('shopify-section-' + sectionKey);
      if (newSection && currentSection) { currentSection.replaceWith(newSection); return; }
      var currentBubble = document.querySelector('.cart-count-bubble');
      var newBubble = doc.querySelector('.cart-count-bubble');
      if (currentBubble && newBubble) currentBubble.replaceWith(newBubble);
    } catch (e) { /* non-fatal */ }
  }

  function updateCartIconFromResponse(cartResponse) {
    if (!cartResponse || !cartResponse.sections) return;
    if (cartResponse.sections['cart-icon-bubble']) {
      replaceSectionOnPage('cart-icon-bubble', cartResponse.sections['cart-icon-bubble']);
    }
    if (cartResponse.sections['cart-live-region-text']) {
      replaceSectionOnPage('cart-live-region-text', cartResponse.sections['cart-live-region-text']);
    }
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'skyyrizon-product-grid', cartData: cartResponse });
    }
  }

  // Legacy shim
  function refreshCartBubble() { /* superseded by updateCartIconFromResponse */ }

  function handleSubmit(form, event, section) {
    event.preventDefault();
    var btn = form.querySelector('.sky-pgrid__add');
    if (!btn || btn.disabled) return;

    setBtnState(btn, 'loading');

    var formData = new FormData(form);
    var routes = (window.Shopify && window.Shopify.routes) || {};
    var addUrl = (routes.cart_add_url || '/cart/add.js') + '?sections=cart-icon-bubble,cart-live-region-text';
    fetch(addUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (e) { throw e; });
        return res.json();
      })
      .then(function (cartResponse) {
        setBtnState(btn, 'done');
        showToast(section, 'Added to cart');
        updateCartIconFromResponse(cartResponse);
        window.setTimeout(function () { setBtnState(btn, null); }, DONE_TIMEOUT);
      })
      .catch(function (err) {
        setBtnState(btn, 'error');
        var msg = (err && err.description) || (err && err.message) || 'Could not add';
        showToast(section, msg);
        window.setTimeout(function () { setBtnState(btn, null); }, DONE_TIMEOUT);
      });
  }

  function initForms(section) {
    var forms = section.querySelectorAll('[data-sky-pgrid-add]');
    forms.forEach(function (form) {
      form.addEventListener('submit', function (e) { handleSubmit(form, e, section); });
    });
  }

  // ── Init ──────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('.sky-pgrid-wrap').forEach(function (section) {
      var grid = section.querySelector('[data-sky-pgrid]');
      if (grid) initGrid(grid, section);
      initForms(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
